import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import api from '../../api';
import { toast } from 'react-toastify';
import {
  ArrowLeft,
  Building,
  MapPin,
  Phone,
  Calendar,
  Clock,
  Info,
  Users,
  Ticket,
  Mail,
  Trash2,
  Save,
  BadgeDollarSign,
  DollarSignIcon,
} from 'lucide-react';

function DetailItem({ icon: Icon, children }) {
  return (
    <li className="flex">
      <Icon className="h-5 w-5 mr-2 text-terrecuite" />
      {children}
    </li>
  );
}

export default function OrganizationDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  // --- États principaux ---
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- Édition du formulaire ---
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    adresse: '',
    telephone: '',
    date_debut_collaboration: '',
    description: '',
    prix_jour:0,
    module_ids: []
  });
  //  all modules
  const [allModules, setAllModules] = useState([]);
  // --- Pagination & filtres utilisateur ---
  const [userPage, setUserPage] = useState(1);
  const usersPerPage = 5;
  const [userSearch, setUserSearch] = useState('');

  // --- Pagination & filtres tickets ---
  const [ticketPage, setTicketPage] = useState(1);
  const ticketsPerPage = 5;
  const [ticketPriorityFilter, setTicketPriorityFilter] = useState('Tous');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');


  // --- Récupération des modules ---
  useEffect(() => {
  api.get('/modules')
    .then(res => setAllModules(res.data))
    .catch(() => toast.error('Erreur lors du chargement des modules'));
}, []);

  // Récupère l'organisation
  useEffect(() => {
    api.get(`/organisations/${id}`)
      .then(res => {
        const data = res.data.organisation;
        if (data) {
          setOrg(data);
          setFormData({
            adresse: data.adresse || '',
            telephone: data.telephone || '',
            date_debut_collaboration: data.date_debut_collaboration || '',
            description: data.description || '',
            prix_jour: data.prix_jour || 0,
            module_ids: data.modules.map(m => m.id),
          });
        } else {
          setError('Organisation introuvable.');
        }
      })
      .catch(() => {
        setError("Erreur lors de la récupération de l'organisation");
        toast.error("Impossible de charger les détails de l'organisation");
      })
      .finally(() => setLoading(false));
  }, [id]);

  // Gestion du formulaire
    const handleChange = e => {
      const { name, value, type, checked } = e.target;
      
      if (name === 'module_ids') {
        const idNum = parseInt(value, 10);
        setFormData(f => ({
          ...f,
          module_ids: checked
            ? [...f.module_ids, idNum]
            : f.module_ids.filter(id => id !== idNum),
        }));
      } else {
        // Gestion standard des autres champs
        setFormData(prev => ({
          ...prev,
          [name]: type === 'checkbox' ? checked : value
        }));
      }
    };


    const handleSave = () => {
      api.post(`/organisations/${id}`, formData)
        .then(res => {
          setOrg(prev => ({...prev, ...res.data.organisation}));
          setEditing(false);
          toast.success(
            res.data.message || 'Mis à jour avec succès',
            { autoClose: 500 }
          );
        })
        .catch(() => {
          toast.error("Erreur lors de la mise à jour de l'organisation");
        });
    };


  // Priorité tickets
  const priorityLabel = code => {
    switch (String(code)) {
      case '1': return 'Faible';
      case '2': return 'Moyenne';
      case '3': return 'Urgent';
      default:  return code;
    }
  };

  // Filtrage & pagination users
// Dans la section de pagination utilisateurs, ajoutez :
    // Filtrage & pagination users
    const users = useMemo(() => {
      const list = org?.utilisateurs || [];
      return list.filter(u =>
        !userSearch.trim() ||
        `${u.prenom} ${u.nom} ${u.login}`.toLowerCase().includes(userSearch.trim().toLowerCase())
      );
    }, [org, userSearch]);

    const paginatedUsers = useMemo(() => {
      const start = (userPage - 1) * usersPerPage;
      return users.slice(start, start + usersPerPage);
    }, [users, userPage]);

    const totalUserPages = Math.ceil(users.length / usersPerPage);

  // Filtrage & pagination tickets
  const tickets = useMemo(() => {
    const list = org?.tickets || [];
    return list.filter(t => {
      const prioMatch = ticketPriorityFilter === 'Tous' || String(t.priorite) === ticketPriorityFilter;
      const d = new Date(t.created_at);
      const fromOK = !dateFromFilter || d >= new Date(dateFromFilter);
      const toOK   = !dateToFilter   || d <= new Date(dateToFilter);
      return prioMatch && fromOK && toOK;
    });
  }, [org, ticketPriorityFilter, dateFromFilter, dateToFilter]);
  const paginatedTickets = useMemo(() => {
    const start = (ticketPage - 1) * ticketsPerPage;
    return tickets.slice(start, start + ticketsPerPage);
  }, [tickets, ticketPage]);
  const totalTicketPages = Math.ceil(tickets.length / ticketsPerPage);

  // États de chargement / erreur
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen w-full bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-terrecuite" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="w-full p-6 bg-red-50 border-l-4 border-red-500">
        <div className="flex items-center">
          <Trash2 className="h-5 w-5 text-red-500 mr-3" />
          <p className="text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-4 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-terrecuite hover:opacity-80 mr-4"
        >
          <ArrowLeft className="h-5 w-5" /> Retour
        </button>
        <h1 className="text-3xl font-bold text-gray-800 flex items-center">
          <Building className="h-8 w-8 mr-2 text-terrecuite" />
          {org.nomOrganisation}
        </h1>
      </div>
  
      {/* Détails & Métadonnées */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Détails (Editable) */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold flex items-center">
              <Info className="h-5 w-5 mr-2 text-terrecuite" /> Détails
            </h2>
            <button
              onClick={() => (editing ? handleSave() : setEditing(true))}
              className="flex items-center px-3 py-1 bg-terrecuite text-white rounded hover:opacity-90"
            >
              <Save className="h-4 w-4 mr-1" /> {editing ? 'Enregistrer' : 'Modifier'}
            </button>
          </div>
          <ul className="space-y-3 text-gray-700">
            <DetailItem icon={MapPin}>
              {editing
                ? <input name="adresse" value={formData.adresse} onChange={handleChange}
                         className="border rounded p-1 w-full" />
                : org.adresse || 'N/A'}
            </DetailItem>
            <DetailItem icon={Phone}>
              {editing
                ? <input name="telephone" value={formData.telephone} onChange={handleChange}
                         className="border rounded p-1 w-full" />
                : org.telephone || 'N/A'}
            </DetailItem>

            <DetailItem icon={BadgeDollarSign}>
              {editing
                ? <input name="prix_jour" value={formData.prix_jour} onChange={handleChange}
                         className="border rounded p-1 w-full" />
                : org.prix_jour || 'N/A'}
            </DetailItem>
            <DetailItem icon={Calendar }>
                {editing ? (
                  <input 
                    type="date" 
                    name="date_debut_collaboration"
                    value={formData.date_debut_collaboration || ''}
                    onChange={handleChange}
                    className="border rounded p-1 w-full"
                    required // you can add this if the field is mandatory
                  />
                ) : (
                  org.date_debut_collaboration ? org.date_debut_collaboration : 'N/A'
                )}
              </DetailItem>

            <DetailItem icon={Info}>
              {editing
                ? <textarea name="description" value={formData.description}
                            onChange={handleChange}
                            className="border rounded p-1 w-full" />
                : org.description || 'N/A'}
            </DetailItem>
            <DetailItem icon={DollarSignIcon}>
              
              {editing ? (
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Modules  :    
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {allModules.map(mod => (
                      <label key={mod.id} className="inline-flex items-center space-x-2">
                        <input
                          type="checkbox"
                          name="module_ids"
                          value={mod.id}
                          checked={formData.module_ids.includes(mod.id)}
                          onChange={handleChange}
                          className="rounded border-gray-300 text-terrecuite"
                        />
                        <span className="text-gray-700">{mod.nom}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ) : (
                (org.modules || []).length > 0
                  ? org.modules.map(m => <span key={m.id} className="inline-block mr-2">• {m.nom}</span>)
                  : 'Aucun module'
              )}
            </DetailItem>

          </ul>
        </div>
  
        {/* Métadonnées */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <Clock className="h-5 w-5 mr-2 text-terrecuite" /> Métadonnées
          </h2>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-center">
              <Clock className="h-5 w-5 mr-2" /> Créé le: {new Date(org.created_at).toLocaleString()}
            </li>
            <li className="flex items-center">
              <Clock className="h-5 w-5 mr-2" /> Mis à jour: {new Date(org.updated_at).toLocaleString()}
            </li>
            <li className="flex items-center">
              <Clock className="h-5 w-5 mr-2" /> Supprimé: {org.deleted_at || 'N/A'}
            </li>
          </ul>
        </div>
      </div>
  
      {/* Users Section */}
      <div className="bg-white p-6 rounded-lg shadow mb-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 space-y-2 md:space-y-0">
          <h2 className="text-xl font-semibold flex items-center">
            <Users className="h-6 w-6 mr-2 text-terrecuite" /> Utilisateurs ({users.length})
          </h2>
          <div className="flex space-x-2">
            <input
              type="text"
              placeholder="Rechercher nom ou login"
              value={userSearch}
              onChange={e => { setUserSearch(e.target.value); setUserPage(1); }}
              className="border rounded p-1"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full table-fixed divide-y divide-gray-200 text-gray-700">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-2 text-center">ID</th>
                <th className="px-4 py-2 text-center">Nom</th>
                <th className="px-4 py-2 text-center">Email</th>
                <th className="px-4 py-2 text-center">Login</th>
                <th className="px-4 py-2 text-center">Téléphone</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedUsers.map(u => (
                <tr
                  key={u.utilisateur_id}
                  className="hover:bg-gray-50 cursor-pointer text-center"
                  onClick={() => navigate(`/admin/employees/${u.utilisateur_id}`)}
                >
                  <td className="px-4 py-2">{u.utilisateur_id}</td>
                  <td className="px-4 py-2">{`${u.prenom} ${u.nom}`}</td>
                  <td className="px-4 py-2 flex items-center justify-center space-x-1">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <span className="truncate">{u.email}</span>
                  </td>
                  <td className="px-4 py-2">{u.login}</td>
                  <td className="px-4 py-2 flex items-center justify-center space-x-1">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span>{u.telephone || 'N/A'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination Users */}
        <div className="flex justify-end space-x-2 mt-4">
          <button
            onClick={() => setUserPage(p => Math.max(p - 1, 1))}
            disabled={userPage === 1}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Précédent
          </button>
          <span className="px-3 py-1">Page {userPage} / {totalUserPages}</span>
          <button
            onClick={() => setUserPage(p => Math.min(p + 1, totalUserPages))}
            disabled={userPage === totalUserPages}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Suivant
          </button>
        </div>
      </div>
  
      {/* Tickets Section */}
      <div className="bg-white p-6 rounded-lg shadow mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold flex items-center">
            <Ticket className="h-6 w-6 mr-2 text-terrecuite" /> Tickets ({tickets.length})
          </h2>
          <div className="flex space-x-2">
            <select
              value={ticketPriorityFilter}
              onChange={e => { setTicketPriorityFilter(e.target.value); setTicketPage(1); }}
              className="border rounded p-1"
            >
              <option value="Tous">Toutes priorités</option>
              <option value="1">Faible</option>
              <option value="2">Moyenne</option>
              <option value="3">Urgent</option>
            </select>
            <input
              type="date"
              value={dateFromFilter}
              onChange={e => { setDateFromFilter(e.target.value); setTicketPage(1); }}
              className="border rounded p-1"
            />
            <input
              type="date"
              value={dateToFilter}
              onChange={e => { setDateToFilter(e.target.value); setTicketPage(1); }}
              className="border rounded p-1"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-gray-700">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-2 text-left">ID</th>
                <th className="px-4 py-2 text-left">Nom</th>
                <th className="px-4 py-2 text-left">Type</th>
                <th className="px-4 py-2 text-left">Priorité</th>
                <th className="px-4 py-2 text-left">Date</th>
              </tr>
            </thead>
            <tbody>
              {paginatedTickets.map(t => (
                <tr
                  key={t.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/admin/task/${t.id}`)}
                >
                  <td className="px-4 py-2">{t.id}</td>
                  <td className="px-4 py-2">{t.nom}</td>
                  <td className="px-4 py-2">{t.type}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      priorityLabel(t.priorite) === 'Urgent'   ? 'bg-red-100 text-red-800' :
                      priorityLabel(t.priorite) === 'Moyenne'  ? 'bg-yellow-100 text-yellow-800' :
                                                                 'bg-gray-100 text-gray-800'
                    }`}>
                      {priorityLabel(t.priorite)}
                    </span>
                  </td>
                  <td className="px-4 py-2">{new Date(t.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination Tickets */}
        <div className="flex justify-end space-x-2 mt-4">
          <button
            onClick={() => setTicketPage(p => Math.max(p - 1, 1))}
            disabled={ticketPage === 1}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Précédent
          </button>
          <span className="px-3 py-1">Page {ticketPage} / {totalTicketPages}</span>
          <button
            onClick={() => setTicketPage(p => Math.min(p + 1, totalTicketPages))}
            disabled={ticketPage === totalTicketPages}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Suivant
          </button>
        </div>
      </div>
    </div>
  );
  
}
